import {
  // action,
  // IObservableValue,
  observable,
  runInAction
} from 'mobx';
// import { observer } from "mobx-react";
import React from 'react';

import { Paginated } from '../adl-gen/common';
import { WithDbId } from '../adl-gen/common/db';
import { makeTableQuery } from '../adl-gen/common/tabular';
import {
  I18nContent,
  Internationalized,
  Lang,
  makeInternationalized,
  // LangCode, // Do not import this
  makeLang
} from '../adl-gen/totalinteraction/futurefit/db';
// TODO move out of admin-ui
import { I18nInput } from '../admin/ui/common/editor';
import { Service } from '../service/service';
import {
  // Select,
  SelectOption
} from '../ui/widgets/select/select';
import { FormValue, newTextField, withValueCallback } from '../ui/widgets/text-input/text-field';

// // tslint:disable-next-line: no-import-side-effect
import { i18next } from './i18next';

/**
 * Global object and Higher-Order Component to manage translated content as fetched from the database.
 * This is for props injection not wrapping.
 *
 * This is data content that should be maintainable by Site Administrators and is
 * not boilerplate text stored in the Client (typically in public/locale). Otherwise
 * Site Admins would have to update the source code...
 */

/// This is what this item *should* look like following ADL generation.
export enum LangCode {
  /// English (fallback/default)
  en = 'en',
  /// Mandarin Chinese (Simplified)
  zh = 'zh',
  // FF2-499
  /// Japanese
  ja = 'ja',
  /// Korean
  ko = 'ko',
  /// French (for dev and test purposes at the start)
  fr = 'fr'
}

/// Global i18n object to implement this interface
interface I18nConfig {
  /// The current global language setting
  langCode: LangCode;

  /// The default language to be used
  defaultLang: Lang;

  /// (Probably unused) - fallback language if different from default
  fallbackLang: Lang;

  /// All available languages system-wide
  availableLangs: Lang[];

  /// Switch global language setting
  changeLanguage(code: LangCode): Promise<void>;

  /// Utility function to translate internationalized content to current language
  t(content: I18nContent): string;
}

/**
 * Singleton Internationalization class.
 */
export class I18n implements I18nConfig {
  private constructor() { } // prevent external instantiation

  private static instance: I18n;

  static getInstance = (service?: Service): I18n => {
    if (!I18n.instance) {
      I18n.instance = new I18n();
      if (service) {
        I18n.instance.initFromService(service).catch(err => {
          // tslint:disable-next-line: no-console
          console.error('Language initialization error: ', err);
          throw new Error(`Error initializing language list: ${err}`);
        });
      }
    }
    return I18n.instance;
  };

  initI18next = async () => {
    // tslint:disable-next-line: no-console
    console.debug('i18next.init() (called with await in i18n.init())');
    /// init: for all options read: https://www.i18next.com/overview/configuration-options
    await i18next.init({
      fallbackLng: 'en',
      debug: false,

      interpolation: {
        escapeValue: false // not needed for react as it escapes by default
      }
    });
  };

  initFromService = async (service: Service) => {
    const langs = await this.getAvailableLangs(service);
    langs
      ? (this.availableLangs = langs.items.map(item => item.value))
      : (this.availableLangs = this.defaultAvailableLangs);
  };

  /// Fetch available languages from the database
  /// Only called in wired-up code
  init = async () => {
    await this.initI18next();
    // tslint:disable-next-line: no-console
    console.debug(`lang=${i18next.services.languageUtils.getLanguagePartFromCode(i18next.language)}`);
    const currentLang = i18next.language;
    if (currentLang && this.availableLangs.find(l => currentLang.startsWith(l.code))) {
      await this.changeLanguage(this.toLang(currentLang));
    }
  };

  getAvailableLangs = async (service: Service): Promise<Paginated<WithDbId<Lang>> | undefined> => {
    const q = makeTableQuery({});
    const availableLangs = await service.availableLangs(q);
    return availableLangs;
  };

  /// English is hardcoded
  private readonly en = makeLang({
    code: LangCode.en,
    name: 'English',
    /// Not using at present
    fallbackLang: LangCode.en
  });

  /// Testing purposes - do not include zh/ko/ja, this way
  /// can test the fetch works and also have something for Storybook
  private readonly defaultAvailableLangs = [
    this.en,
    makeLang({
      code: LangCode.fr,
      name: 'Français',
      /// Not using at present
      fallbackLang: LangCode.en
    })
  ];

  availableLangs = this.defaultAvailableLangs;

  /// Current lang setting
  @observable
  langCode = LangCode.en;

  defaultLang = this.en;
  fallbackLang = this.en;

  changeLanguage = async (code: LangCode): Promise<void> => {
    await i18next.changeLanguage(code);
    runInAction(() => (this.langCode = code));
  };

  /// This might get passed 'en-US', 'en-GB' etc
  private readonly toLang = (lang: string): LangCode => {
    if (lang.startsWith('en')) {
      return LangCode.en;
    }
    if (lang.startsWith('fr')) {
      return LangCode.fr;
    }
    if (lang.startsWith('ja')) {
      return LangCode.ja;
    }
    if (lang.startsWith('ko')) {
      return LangCode.ko;
    }
    if (lang.startsWith('zh')) {
      return LangCode.zh;
    }
    return LangCode.en;
  };

  private readonly translate = <T extends {}>(i15d: Internationalized<T>, lang: LangCode): T => {
    if (lang === LangCode.zh && i15d.zh !== null) {
      return i15d.zh;
    }
    if (lang === LangCode.ko && i15d.ko !== null) {
      return i15d.ko;
    }
    if (lang === LangCode.ja && i15d.ja !== null) {
      return i15d.ja;
    }
    if (lang === LangCode.fr && i15d.fr !== null) {
      return i15d.fr;
    }
    return i15d.en;
  };

  t = (content: I18nContent, lang?: LangCode): string => {
    const langToUse = lang === undefined ? this.langCode : lang;
    return this.translate(content.value, langToUse);
  };

  /// Options for Select component
  options = (): SelectOption[] => {
    return this.availableLangs.map(lang => ({ value: lang.code, label: lang.name }));
  };

  getLangFromCode = (code: LangCode): Lang => {
    const selectedLang = this.availableLangs.find(l => l.code === code);
    return selectedLang ? selectedLang : this.defaultLang;
  };
}

export type TranslateFn = (content: I18nContent, lang?: LangCode) => string;

/// HOC injected props - in TypeScript, Component prop interfaces have to extend this interface
export interface WithI18nProps {
  /// Switch current global language setting
  changeLanguage(code: LangCode): Promise<void>;

  /// Translate internationalized content to current language
  t: TranslateFn;

  /// Options for Select component
  i18nOptions(): SelectOption[];

  /// Get current setting
  currentLangCode(): LangCode;

  defaultLang(): Lang;
  availableLangs(): Lang[];
  getLangFromCode(code: LangCode): Lang;

  // Editor support
  initI18nEmpty(field: I18nInput, callback?: (f: FormValue<string>) => void): I18nInput;
  initI18nFromInput(field: I18nInput, input: I18nContent, callback?: (f: FormValue<string>) => void): void;
  isI18nValid(field: I18nInput, emptyAllowed?: boolean): boolean;
  isI18nNonEmpty(field: I18nInput): boolean;
  makeI18nContent(field: I18nInput): I18nContent;
  equals(a: I18nContent, b: I18nContent): boolean;
}

/// Another interface for any State not directly accessed by wrapped component if required (eg. current lang)
interface WithI18nState {
  i18nConfig: I18n;
}

/// HOC - inject WithI18nProps into arbitrary React Component by 'replacing' it with the WithI18n class
// Note the generics syntax - and use of Omit<>
export const withI18n = <P extends WithI18nProps>(Component: React.ComponentType<P>) =>
  class WithI18n extends React.Component<Omit<P, keyof WithI18nProps>, WithI18nState> {
    i18nState: WithI18nState = {
      i18nConfig: I18n.getInstance()
    };

    availableLangs = (): Lang[] => this.i18nState.i18nConfig.availableLangs;
    defaultLang = (): Lang => this.i18nState.i18nConfig.defaultLang;

    render() {
      /// NB Component should be an observer...
      return (
        <Component
          {...(this.props as P)}
          changeLanguage={async code => this.i18nState.i18nConfig.changeLanguage(code)}
          t={(content, lang) => this.i18nState.i18nConfig.t(content, lang)}
          i18nOptions={this.i18nState.i18nConfig.options}
          currentLangCode={() => this.i18nState.i18nConfig.langCode}
          availableLangs={this.availableLangs}
          defaultLang={this.defaultLang}
          getLangFromCode={code => this.i18nState.i18nConfig.getLangFromCode(code)}
          initI18nEmpty={initI18nEmpty(this.availableLangs(), this.defaultLang())}
          initI18nFromInput={initI18nFromInput(this.availableLangs(), this.defaultLang())}
          isI18nValid={isI18nValid(this.availableLangs(), this.defaultLang())}
          isI18nNonEmpty={isI18nNonEmpty}
          makeI18nContent={makeI18nContent(this.availableLangs(), this.defaultLang())}
          equals={equals}
        />
      );
    }
  };

const initI18nEmpty = (availableLangs: Lang[], defaultLang: Lang) => (
  field: I18nInput,
  callback?: (f: FormValue<string>) => void
): I18nInput => {
  return availableLangs.reduce((input: I18nInput, l: Lang) => {
    if (l.code === defaultLang.code) {
      return input;
    } else {
      if (callback) {
        return { ...input, [l.code]: withValueCallback(newTextField(), callback) };
      } else {
        return { ...input, [l.code]: newTextField() };
      }
    }
  }, field);
};

const initI18nFromInput = (availableLangs: Lang[], defaultLang: Lang) => (
  field: I18nInput,
  input: I18nContent,
  callback?: (f: FormValue<string>) => void
): void => {
  field.en.onChange(input.value.en);
  availableLangs.map(l => {
    if (l.code !== defaultLang.code) {
      callback ? (field[l.code] = withValueCallback(newTextField(), callback)) : (field[l.code] = newTextField());
      if (input.value[l.code] !== null) {
        field[l.code].onChange(input.value[l.code]);
      }
    }
  });
};

const isI18nValid = (availableLangs: Lang[], defaultLang: Lang) => (
  field: I18nInput,
  emptyAllowed?: boolean
): boolean => {
  if (emptyAllowed && (field.en.get().kind === 'empty' || field.en.value().length === 0)) {
    return true;
  }
  if (field.en.getValid() === undefined) {
    return false;
  } else {
    for (const l of availableLangs) {
      if (l.code !== defaultLang.code && field[l.code] !== null && field[l.code] === undefined) {
        return false;
      }
    }
  }
  return true;
};

const isI18nNonEmpty = (field: I18nInput): boolean => {
  return field.en.get().kind !== 'empty' && field.en.value().length > 0;
};

const makeI18nContent = (availableLangs: Lang[], defaultLang: Lang) => (field: I18nInput): I18nContent => {
  const output = makeInternationalized({ en: field.en.value() });
  for (const l of availableLangs) {
    /// Only do this for non-en value which should already be set
    if (l.code !== defaultLang.code) {
      field[l.code] !== null && field[l.code].value() !== ''
        ? (output[l.code] = field[l.code].value())
        : /// Needed to ensure fallback lang is used
        (output[l.code] = null);
    }
  }
  return { kind: 'text', value: output };
};

const equals = (a: I18nContent, b: I18nContent): boolean => {
  if (a.value.en.toLocaleLowerCase().localeCompare(b.value.en.toLocaleLowerCase()) !== 0) {
    return false;
  }
  for (const field of Object.keys(a.value)) {
    if (field === 'en') {
      continue;
    }
    if (a.value[field] === null && b.value[field] === null) {
      continue;
    }
    if (a.value[field] !== null && b.value[field] !== null) {
      if (a.value[field].toLocaleLowerCase().localeCompare(b.value[field].toLocaleLowerCase()) !== 0) {
        return false;
      }
      continue;
    }
    if (a.value[field] === null || b.value[field] === null) {
      return false;
    }
  }
  return true;
};

// export const TranslationContext = createContext<WithI18nState|null>(null);

// // Do a hooks version...
// export const useI18n = (): WithI18nProps => {
//     const [i18nState, ] = useState<WithI18nState>({ i18nConfig: I18n.getInstance() });

//     const availableLangs = (): Lang[] => i18nState.i18nConfig.availableLangs;
//     const defaultLang = (): Lang => i18nState.i18nConfig.defaultLang;

//     return {
//         changeLanguage: async (code) => i18nState.i18nConfig.changeLanguage(code),
//         t: (content, lang) => i18nState.i18nConfig.t(content, lang),
//         i18nOptions: i18nState.i18nConfig.options,
//         currentLangCode: () => i18nState.i18nConfig.langCode,
//         availableLangs,
//         defaultLang,
//         getLangFromCode: (code) => i18nState.i18nConfig.getLangFromCode(code),
//         initI18nEmpty: initI18nEmpty(availableLangs(), defaultLang()),
//         initI18nFromInput: initI18nFromInput(availableLangs(), defaultLang()),
//         isI18nValid: isI18nValid(availableLangs(), defaultLang()),
//         isI18nNonEmpty,
//         makeI18nContent: makeI18nContent(availableLangs(), defaultLang()),
//         equals,
//     };
// }



// WEBPACK FOOTER //
// ./src/internationalization/i18n-hoc.tsx